import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import storage from "../../../utils/storage";
import { Link } from "react-router-dom";
import classNames from 'classnames';
import { useDispatch, useSelector } from "react-redux";
import appActions from "../../../appActions";
import PrimeTable from "../../../components/PrimeTable";
import { isAdminRole } from "../../../utils/mics";
import TaskNotificationFilterBar from "./TaskNotificationFilterBar";
import TaskNotificationHeader from "./TaskNotificationHeader";
import Sidebar from "../../../components/Sidebar";

const TaskNotificationList = () => {

    const dispatch = useDispatch();

    const userInfo = useMemo(() => JSON.parse(storage.get('loginUser')), []);
  //  const { userRole, userId, siteId } = userInfo;

    const { taskNotificationList, taskNotificationDetails } = useSelector((state) => state?.tasknotification);

    const { storeList } = useSelector((state) => state?.common);

    const defaultParams = useMemo(() => ({
        pageno: 1,
        pagesize: 10,
        sort: 'createdOn',
        sortOrder : -1,
    }), []);

    const [formValues, setFormValues] = useState(defaultParams);
    const [filterParams, setFilterParams] = useState({});

    // New states for dialog control and selected user
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const [selectedTaskNotification, setSelectedTaskNotification] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const [isCopyMode, setIsCopyMode] = useState(false);

    const [taskNotificationRecords, setTaskNotificationRecords] = useState([]);

    const fetchTaskNotification = useCallback((params = {}) => {
        dispatch(appActions.TASKNOTIFICATION_GET_ALL_REQUEST({ ...defaultParams, ...params }));
    }, [dispatch, defaultParams]);

    const handleFilterTask = useCallback((params) => {
        setFilterParams(params);
        fetchTaskNotification(params);
    }, [fetchTaskNotification]);

    useEffect(() => {
        fetchTaskNotification();
    }, [dispatch]);
    
    useEffect(() => {
        if(taskNotificationList && storeList) {
            const updatedTaskNotificationData = taskNotificationList?.data?.map(taskNotification => {
                let siteName = ''; // Use let to allow reassignment

                // Check for matches in sitesList
                storeList?.forEach(site => {
                    if (taskNotification.siteId == site.siteId) {
                        siteName = site.title; // Assign the matched site title
                        return; // Exit the forEach loop if a match is found
                    }
                });

                // Return the updated task with the new description
                return {
                    ...taskNotification,
                    siteName
                };
            })

            setTaskNotificationRecords({...taskNotificationList, data: updatedTaskNotificationData});
        }
    }, [taskNotificationList]);

    const handleCopyRedirect = (record) => {
        const data = taskNotificationRecords?.data?.find((t) => t.id === record.id);
        setSelectedTaskNotification(data);
        setIsViewMode(false);
        setIsEditMode(false);
        setIsCopyMode(true);
        setIsDialogVisible(true); // Open dialog in view mode
    };

    const handleViewRedirect = (record) => {
        const data = taskNotificationRecords?.data?.find((t) => t.id === record.id);
        setSelectedTaskNotification(data);
        setIsViewMode(true);
        setIsEditMode(false);
        setIsDialogVisible(true); // Open dialog in view mode
    };

    const handleEditRedirect = (record) => {
        const data = taskNotificationRecords?.data?.find((t) => t.id === record.id);
        setSelectedTaskNotification(data);
        setIsEditMode(true);
        setIsViewMode(false);
        setIsDialogVisible(true); // Open dialog in edit mode
    };

    const handleDelete = useCallback((record) => {
        const { id } = record;
        dispatch(appActions.TASKNOTIFICATION_DELETE_REQUEST({ id }));
    }, [dispatch]);

    const handleDialogClose = () => {
        setIsDialogVisible(false);
        setSelectedTaskNotification(null);
    };

    const handleDialogSave = () => {
        fetchTaskNotification();
        setIsDialogVisible(false);
    }

    const handleSearch = useCallback(
        (params) => {

            const searchParams = {
                ...params,
                pagesize: params.pagesize || defaultParams?.pagesize,
                pageno: params.pageno || defaultParams?.pageno,
                sort: params.sort || defaultParams?.sort,
                sortOrder: params.sortOrder || defaultParams?.sortOrder,
            };

            dispatch(appActions.TASKNOTIFICATION_GET_ALL_REQUEST(searchParams));
        },
        [dispatch],
    );

    const onFilterChangeCallback  = useCallback(
        (event) => {

            const { sortField, sortOrder, rows, page } = event;
            
            const params = {...formValues, pagesize: rows, pageno: page, sort: sortField, sortOrder: sortOrder };

            setFormValues(prevFormValues => ({
                ...prevFormValues,
                sort: sortField, 
                sortOrder: sortOrder,
                pagesize: rows,
                pageno: page
            }));

            handleSearch(params); 
        },

        [handleSearch, formValues],
    );

    
    useEffect(() => {
        if (taskNotificationDetails?.action === 'delete' || taskNotificationDetails?.action === 'create' || taskNotificationDetails?.action === 'update') {
            fetchTaskNotification();
        }
    }, [taskNotificationDetails, fetchTaskNotification]);

    const columns = [
        { field: 'title', header: 'Subject', sortable: true},
        { field: 'siteId', header: 'Site', sortable: true},
        { field: 'siteName', header: 'Site Name', sortable: true},
        { field: 'receivedDateTime', header: 'Date & Time Received at Site', sortable: true},
        { field: 'markActionDateTime', header: 'Date & Time Mark Actioned', sortable: true},
    ];

    const actionTemplate = useCallback(
        (rowData) => (
            <div className="flex gap-10 justify-content-center">
                <span className="cursor-pointer tp" onClick={() => handleViewRedirect(rowData)} data-pr-tooltip="View">
                    <i className="pi pi-eye text-blue-500 text-base"></i>
                </span>
                <span className="cursor-pointer tp" onClick={() => handleEditRedirect(rowData)} data-pr-tooltip="Edit">
                    <i className="pi pi-pencil text-blue-500 text-base"></i>
                </span>
                <span className="cursor-pointer tp" onClick={() => handleCopyRedirect(rowData)} data-pr-tooltip="Copy">
                    <i className="pi pi-copy text-blue-500 text-base"></i>
                </span>
                <span className="cursor-pointer tp" onClick={() => handleDelete(rowData)} data-pr-tooltip="Delete">
                    <i className="pi pi-times-circle text-base text-danger"/>
                </span>
            </div>
        ),
        [handleCopyRedirect, handleViewRedirect, handleEditRedirect, handleDelete],
    );

    return (
        <div className="w-full">
           
            <TaskNotificationHeader />
            <div className="flex flex-min-height">
                <Sidebar />
                <div className="p-grid w-full pl-60px">
                    <h3 className="title">Task Notification Report</h3>
                    <TaskNotificationFilterBar
                        handleFilter={handleFilterTask}
                        filterParams={filterParams}
                        userInfo={userInfo}
                        handleSearch={handleSearch}
                    />
                    <PrimeTable
                        list={taskNotificationRecords}
                        columns={columns}
                        // actionColumn={actionTemplate}
                        tableName='site_group_management'
                        isLazy={true}
                        onFilterChangeCallback={(e) => {
                            onFilterChangeCallback(e);
                        }}
                        isExport={false}
                    />
                </div>

            </div>
        </div>
    );
}

export default TaskNotificationList;
